import { useEffect, useRef, useState } from 'react';
import { api } from './api';
import { ListEditor } from './Routine';
import { useI18n } from './i18n';
import { trackEvent } from './analytics';

// The feelings a trader can hit the brakes on, each with a message and things to do now.
export const RESET_FEELINGS = [
  {
    id: 'FOMO', face: '😱', label: 'FOMO',
    message: 'The move you missed is gone. Wait for your setup to come back to you.',
    actions: ['Take your hand off the mouse', 'Look away from the chart for 2 minutes', 'Check: does this trade meet every rule in your plan?'],
  },
  {
    id: 'Tilt', face: '🌀', label: 'Tilt',
    message: "You're not trading your plan right now, you're trading your feelings. Stop for a moment.",
    actions: ['Stand up and walk away from your desk for 10 minutes', 'Drink a glass of water', 'Take 10 slow, deep breaths'],
  },
  {
    id: 'Revenge trading', face: '😡', label: 'Revenge',
    message: "The market doesn't owe you anything. Trying to win it back fast is how small losses become big ones.",
    actions: ['Close your trading platform for 15 minutes', 'Go for a walk outside', 'Write down what happened and how it made you feel'],
  },
  {
    id: 'Greed', face: '🤑', label: 'Greed',
    message: "A good trade doesn't need to become a great one. Stick to the target you planned.",
    actions: ['Take profit at your planned target', "Don't add size to a winner on impulse", 'Step back and re-read your plan'],
  },
  {
    id: 'Fear / hesitation', face: '😰', label: 'Fear',
    message: "Fear is information, not an order. If it's your setup, trust your plan. If it isn't, stay out.",
    actions: ['Breathe slowly for one minute', 'Re-read your entry rules', 'Trade your smallest size, or skip this one'],
  },
];

export const BUILT_IN_QUOTES = [
  'Live to trade another day.',
  "Another trade will come. Don't chase this one.",
  'The market will be here tomorrow. Make sure your account is too.',
  "You don't need to make it back today.",
  'Protect your capital first. Profits come second.',
  'No setup, no trade.',
  'Missing a trade costs nothing. Forcing one can cost everything.',
  'Your edge plays out over hundreds of trades, not this one.',
  'Discipline is choosing what you want most over what you want right now.',
  'Patience is a position too.',
];

const BREATH = [
  { label: 'Breathe in', className: 'grow' },
  { label: 'Hold', className: 'hold-big' },
  { label: 'Breathe out', className: 'shrink' },
  { label: 'Hold', className: 'hold-small' },
];
const BREATH_SECONDS = 4;

function Breathing() {
  const { t } = useI18n();
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const phase = BREATH[Math.floor(tick / BREATH_SECONDS) % BREATH.length];
  const count = BREATH_SECONDS - (tick % BREATH_SECONDS);
  return (
    <div className="breathing" aria-live="polite">
      <div className={`breath-circle ${phase.className}`}>
        <span className="breath-label">{t(phase.label)}</span>
        <span className="breath-count">{count}</span>
      </div>
    </div>
  );
}

export function ResetOverlay({ feeling, customActions, customQuotes, vision, onTimeout, onWrite, onClose, onCustomize }) {
  useEffect(() => trackEvent('Reset button'), []);
  const { t } = useI18n();
  const dialog = useRef(null);
  const quotes = [...BUILT_IN_QUOTES, ...customQuotes];
  // A different line each time; the trader's own quotes come up just as often as the built-in ones.
  const [quote] = useState(() => {
    const pool = customQuotes.length ? (Math.random() < 0.5 ? customQuotes : BUILT_IN_QUOTES) : quotes;
    return pool[Math.floor(Math.random() * pool.length)];
  });

  useEffect(() => {
    dialog.current?.focus();
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="reset-backdrop">
      <div ref={dialog} tabIndex={-1} className="reset" role="dialog" aria-modal="true" aria-labelledby="reset-title">
        <div className="reset-head">
          <span className="reset-face" aria-hidden="true">{feeling.face}</span>
          <div>
            <p className="reset-kicker">{t("You noticed it. That's the hard part.")}</p>
            <h2 id="reset-title">
              {feeling.label === 'Revenge' ? t('Feeling the urge to revenge trade?') : t(`Feeling ${feeling.label}?`)}
            </h2>
          </div>
        </div>
        <p className="reset-message">{t(feeling.message)}</p>

        <div className="reset-body">
          <Breathing />
          <div className="reset-actions">
            <span className="reset-section-label">{t('Do this right now')}</span>
            <ul>
              {[...feeling.actions.map((a) => t(a)), ...customActions].map((a) => <li key={a}>{a}</li>)}
            </ul>
          </div>
        </div>

        <blockquote className="reset-quote">“{t(quote)}”</blockquote>

        {vision && (
          <p className="reset-vision"><span className="reset-section-label">{t('Remember why')}</span>{vision}</p>
        )}

        <div className="reset-buttons">
          <button type="button" onClick={() => onTimeout(15)}>{t('Start a 15-minute timeout')}</button>
          <button type="button" className="secondary" onClick={() => onTimeout(5)}>{t('5-minute timeout')}</button>
          <button type="button" className="secondary" onClick={onWrite}>{t("Write what I'm feeling")}</button>
          <button type="button" className="secondary" onClick={onClose}>{t("I'm okay now")}</button>
        </div>
        <button type="button" className="link-button reset-customize" onClick={onCustomize}>{t('Customize my reset')}</button>
      </div>
    </div>
  );
}

export function ResetEditor({ routine, onSaved, onClose, onSessionExpired }) {
  const { t } = useI18n();
  const [actions, setActions] = useState(routine.resetActions || []);
  const [quotes, setQuotes] = useState(routine.quotes || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const dialog = useRef(null);

  useEffect(() => {
    dialog.current?.focus();
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function save() {
    setSaving(true);
    setError('');
    try {
      const clean = (list) => list.map((x) => x.trim()).filter(Boolean);
      const saved = await api('/routine', { method: 'PUT', body: { resetActions: clean(actions), quotes: clean(quotes) } });
      onSaved(saved);
    } catch (err) {
      if (err.status === 401) return onSessionExpired();
      setError(err.message);
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop reset-editor-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={dialog} tabIndex={-1} className="modal" role="dialog" aria-modal="true" aria-labelledby="reset-edit-title">
        <div className="modal-header">
          <h2 id="reset-edit-title">{t('Customize my reset')}</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label={t('Close')}>&times;</button>
        </div>
        <p className="hint">
          {t('These show up every time you tap a feeling on the Live session, alongside the built-in suggestions and quotes.')}
        </p>
        <ListEditor label={t('My own things to do')} items={actions} onChange={setActions}
          placeholder={t('e.g. Call my accountability partner')} />
        <ListEditor label={t('My own quotes and reminders')} items={quotes} onChange={setQuotes}
          placeholder={t("e.g. I trade for my family, not for today's P&L")} />
        {error && <p className="error">{error}</p>}
        <div className="modal-actions">
          <button type="button" onClick={save} disabled={saving}>{saving ? t('Saving…') : t('Save')}</button>
          <button type="button" className="secondary" onClick={onClose} disabled={saving}>{t('Cancel')}</button>
        </div>
      </div>
    </div>
  );
}
